/* Vista js bundle -- GENERATED by tools/build.py. Do not edit.
   Source: src/js/  (2 files)
   Edit a source part, then run: python tools/build.py
*/

/* ---- src/js/00-core.js ---- */
/* ==========================================================================
   00 · CORE
   Shared utilities. Everything else in src/js/ depends on window.Vista.
   No libraries. No build step beyond concatenation.
   ========================================================================== */

window.Vista = window.Vista || {};

(function (V) {
  'use strict';

  /* ---- selection ------------------------------------------------------- */

  V.$ = function (sel, root) { return (root || document).querySelector(sel); };
  V.$$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* ---- events ---------------------------------------------------------- */

  V.on = function (el, type, fn, opts) {
    if (el) el.addEventListener(type, fn, opts);
    return function () { if (el) el.removeEventListener(type, fn, opts); };
  };

  V.emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent('vista:' + name, {
      detail: detail, bubbles: true
    }));
  };

  V.debounce = function (fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait || 200);
    };
  };

  /* Coalesces bursts of scroll/resize work into one frame. */
  V.raf = function (fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; fn(); });
    };
  };

  /* ---- money ----------------------------------------------------------- */

  /* Shopify hands us the shop's money format in the theme layout. Formatting
     client-side keeps cart updates consistent with server-rendered prices. */
  V.formatMoney = function (cents) {
    var format = (window.Shopify && window.Shopify.moneyFormat) || '${{amount}}';
    var value = (cents / 100);

    function withDelimiters(num, precision, thousands, decimal) {
      num = (num || 0).toFixed(precision);
      var parts = num.split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? intPart + decimal + parts[1] : intPart;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return '';
      }
    });
  };

  /* ---- translations ---------------------------------------------------- */

  /* Strings the JS needs are serialised into <script id="vista-strings"> by
     the layout, so nothing user-facing is hard-coded in English here. */
  V.strings = {};
  V.t = function (key, fallback) {
    return V.strings[key] || fallback || key;
  };

  /* ---- scroll lock ----------------------------------------------------- */

  var lockCount = 0;

  V.lockScroll = function () {
    if (lockCount++ > 0) return;
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--v-scrollbar-w', sbw + 'px');
    document.body.classList.add('v-scroll-locked');
  };

  V.unlockScroll = function () {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.classList.remove('v-scroll-locked');
  };

  /* ---- focus management ------------------------------------------------ */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  V.focusables = function (root) {
    return V.$$(FOCUSABLE, root).filter(function (el) {
      return el.offsetWidth || el.offsetHeight || el.getClientRects().length;
    });
  };

  /* Traps Tab inside `container` until the returned function is called.
     Restores focus to whatever was focused when the trap opened. */
  V.trapFocus = function (container, initial) {
    var previous = document.activeElement;

    function onKey(e) {
      if (e.key !== 'Tab') return;
      var items = V.focusables(container);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    var target = initial || V.focusables(container)[0] || container;
    // Wait a frame: focusing an element mid-transition can cancel the animation.
    requestAnimationFrame(function () { target.focus({ preventScroll: true }); });

    return function release() {
      document.removeEventListener('keydown', onKey);
      if (previous && previous.focus) previous.focus({ preventScroll: true });
    };
  };

  /* ---- fetch helpers --------------------------------------------------- */

  /* Fetches a rendered section from Shopify's Section Rendering API. Used by
     cart drawer, facets and pagination so we never re-implement markup in JS. */
  V.fetchSection = function (sectionId, params) {
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    if (params) {
      Object.keys(params).forEach(function (k) { url.searchParams.set(k, params[k]); });
    }
    return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  };

  V.postJSON = function (url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw Object.assign(new Error(data.description || r.statusText), data);
        return data;
      });
    });
  };

  /* ---- reveal ---------------------------------------------------------- */

  function initReveals(root) {
    var els = V.$$('[data-reveal]:not(.is-in)', root || document);
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    els.forEach(function (el) {
      // An element inside a horizontal scroller can sit permanently outside the
      // viewport -- slide 3 of a carousel is never "scrolled into view" in the
      // vertical sense, so the observer never fires and the element stays at
      // opacity 0 for the life of the page. Reveal those immediately instead.
      // Found this the hard way: testimonial slides 2 and 3 rendered blank.
      if (el.closest('.v-rail, .v-marquee, .v-drawer, [data-no-reveal-observe]')) {
        el.classList.add('is-in');
        return;
      }
      io.observe(el);
    });
  }
  V.initReveals = initReveals;

  /* ---- boot ------------------------------------------------------------ */

  function boot() {
    // Marks that JS is alive. Reveal animations are gated on this class so a
    // script failure can never leave content permanently invisible.
    document.documentElement.classList.add('v-js');

    var strings = document.getElementById('vista-strings');
    if (strings) {
      try { V.strings = JSON.parse(strings.textContent); } catch (e) { /* keep defaults */ }
    }

    initReveals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: re-run section-agnostic setup when a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
  });

})(window.Vista);

/* ---- src/js/60-vista-hero.js ---- */
/* ==========================================================================
   60 · VISTA HERO — slider layout only
   Scroll-snap navigation, dot state, optional auto-advance.
   No library. The scrolling itself is CSS; JS only moves the scroll position
   and mirrors state into the controls, so with JS disabled the slider is
   still a usable horizontal scroller.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia(REDUCED).matches;
  }

  if (!window.customElements || typeof HTMLElement === 'undefined') return;

  class VistaHeroSliderElement extends HTMLElement {
    /* The constructor must stay free of layout work — the element can be
       constructed by the parser before its children exist. */
    constructor() {
      super();
      this.index = 0;
      this.slides = [];
      this.dots = [];
      this.timer = null;
      this.userPaused = false;
      this.stopped = false;
      this._bound = false;
    }

    connectedCallback() {
      this.track = this.querySelector('.v-hero__slides');
      if (!this.track) return;

      this.refresh();
      if (this._bound) return;
      this._bound = true;

      this.onScroll = this.rafThrottle(this.syncFromScroll.bind(this));
      this.onKeydown = this.handleKeydown.bind(this);
      this.onPointerDown = this.handleInteraction.bind(this);
      this.onEnter = this.pause.bind(this);
      this.onLeave = this.resume.bind(this);
      this.onVisibility = this.handleVisibility.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.resume.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      this.stopAuto();
      if (!this._bound) return;
      this._bound = false;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onEnter);
      this.removeEventListener('focusout', this.onLeave);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /* Re-read the DOM. Called on connect and whenever the theme editor
       re-renders the section under us. */
    refresh() {
      this.track = this.querySelector('.v-hero__slides');
      if (!this.track) return;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('.v-hero__slide'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-hero-dot]'));
      this.prevBtn = this.querySelector('[data-hero-prev]');
      this.nextBtn = this.querySelector('[data-hero-next]');
      this.toggleBtn = this.querySelector('[data-hero-toggle]');

      if (this.index >= this.slides.length) this.index = 0;

      var self = this;
      if (this.prevBtn && !this.prevBtn.dataset.vBound) {
        this.prevBtn.dataset.vBound = '1';
        this.prevBtn.addEventListener('click', function () {
          self.handleInteraction();
          self.go(self.index - 1);
        });
      }
      if (this.nextBtn && !this.nextBtn.dataset.vBound) {
        this.nextBtn.dataset.vBound = '1';
        this.nextBtn.addEventListener('click', function () {
          self.handleInteraction();
          self.go(self.index + 1);
        });
      }
      this.dots.forEach(function (dot) {
        if (dot.dataset.vBound) return;
        dot.dataset.vBound = '1';
        dot.addEventListener('click', function () {
          self.handleInteraction();
          self.go(parseInt(dot.getAttribute('data-hero-dot'), 10) || 0);
        });
      });
      if (this.toggleBtn && !this.toggleBtn.dataset.vBound) {
        this.toggleBtn.dataset.vBound = '1';
        this.toggleBtn.addEventListener('click', function () {
          /* The button reads "paused" whenever anything has stopped the
             auto-advance, so pressing it must be able to clear *any* of
             those reasons — not just its own flag. */
          var paused = self.userPaused || self.stopped;
          self.userPaused = !paused;
          self.stopped = false;
          self.reflectToggle();
          if (self.userPaused) self.stopAuto();
          else self.startAuto();
        });
      }

      this.setState(this.index);
      this.startAuto();
    }

    /* ---- navigation --------------------------------------------------- */

    go(target, instant) {
      var count = this.slides.length;
      if (count < 2) return;

      /* Wrapping is what a hero should do, but smooth-scrolling the whole
         strip back to slide 1 looks broken — jump instead. */
      var wrapped = target < 0 || target > count - 1;
      var next = ((target % count) + count) % count;
      var slide = this.slides[next];
      if (!slide) return;

      var behavior = (instant || wrapped || prefersReducedMotion()) ? 'auto' : 'smooth';
      try {
        this.track.scrollTo({ left: slide.offsetLeft, behavior: behavior });
      } catch (e) {
        this.track.scrollLeft = slide.offsetLeft;
      }
      this.setState(next);
    }

    syncFromScroll() {
      var width = this.track.clientWidth;
      if (!width) return;
      var i = Math.round(this.track.scrollLeft / width);
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      if (i !== this.index) this.setState(i);
    }

    setState(i) {
      this.index = i;
      this.dots.forEach(function (dot, n) {
        var active = n === i;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.slides.forEach(function (slide, n) {
        /* Off-screen slides stay in the DOM (scroll-snap needs them) but are
           taken out of the tab order so Tab does not walk into a hidden CTA. */
        var hidden = n !== i;
        slide.querySelectorAll('a[href], button').forEach(function (el) {
          if (hidden) el.setAttribute('tabindex', '-1');
          else el.removeAttribute('tabindex');
        });
      });
    }

    handleKeydown(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.handleInteraction();
        this.go(this.index + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.handleInteraction();
        this.go(this.index - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.handleInteraction();
        this.go(0, true);
      } else if (e.key === 'End') {
        e.preventDefault();
        this.handleInteraction();
        this.go(this.slides.length - 1, true);
      }
    }

    /* ---- auto-advance --------------------------------------------------- */

    get autoplayEnabled() {
      return this.dataset.autoplay === 'true' &&
        this.slides.length > 1 &&
        !prefersReducedMotion();
    }

    get interval() {
      var ms = parseInt(this.dataset.interval, 10);
      return isNaN(ms) ? 6000 : Math.max(3000, ms);
    }

    startAuto() {
      this.stopAuto();
      if (!this.autoplayEnabled || this.stopped || this.userPaused || document.hidden) return;
      var self = this;
      this.timer = setInterval(function () { self.go(self.index + 1); }, this.interval);
    }

    stopAuto() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    pause() { this.stopAuto(); }

    /* focusout fires before the next element takes focus, so document.
       activeElement is briefly <body>. Defer a tick before deciding whether
       focus really left the slider. */
    resume() {
      var self = this;
      setTimeout(function () {
        if (self.contains(document.activeElement)) return;
        if (self.matches(':hover')) return;
        self.startAuto();
      }, 0);
    }

    /* A deliberate move by the visitor ends the auto-advance for good —
       nothing is more annoying than a hero that yanks itself away. */
    handleInteraction() {
      this.stopped = true;
      this.stopAuto();
      this.reflectToggle();
    }

    reflectToggle() {
      if (!this.toggleBtn) return;
      var paused = this.userPaused || this.stopped;
      this.toggleBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      var label = paused
        ? this.toggleBtn.getAttribute('data-label-play')
        : this.toggleBtn.getAttribute('data-label-pause');
      if (label) this.toggleBtn.setAttribute('aria-label', label);
    }

    handleVisibility() {
      if (document.hidden) this.stopAuto();
      else this.startAuto();
    }

    /* ---- theme editor --------------------------------------------------- */

    handleBlockSelect(e) {
      if (!this.contains(e.target)) return;
      this.stopAuto();
      var i = this.slides.indexOf(e.target);
      if (i > -1) this.go(i, true);
    }

    /* ---- util ------------------------------------------------------------ */

    rafThrottle(fn) {
      var queued = false;
      return function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () { queued = false; fn(); });
      };
    }
  }

  if (!window.customElements.get('vista-hero-slider')) {
    window.customElements.define('vista-hero-slider', VistaHeroSliderElement);
  }

  /* Theme editor: a re-rendered section gives us brand-new nodes, so the
     element's connectedCallback fires on its own. A section that is merely
     re-ordered keeps its nodes — refresh() covers that case. */
  document.addEventListener('shopify:section:load', function (e) {
    var els = (e.target || document).querySelectorAll
      ? (e.target || document).querySelectorAll('vista-hero-slider')
      : [];
    Array.prototype.forEach.call(els, function (el) {
      if (typeof el.refresh === 'function') el.refresh();
    });
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var els = (e.target || document).querySelectorAll
      ? (e.target || document).querySelectorAll('vista-hero-slider')
      : [];
    Array.prototype.forEach.call(els, function (el) {
      if (typeof el.stopAuto === 'function') el.stopAuto();
    });
  });
})();
